import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { circularProgressSizeProps } from '@opengov/capital-mui-theme';
import { Close as CloseIcon, Delete as DeleteIcon } from '@opengov/react-capital-assets';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { getFragmentData, graphql } from '../../../../../gql-generated/hasura';
import { DeleteConfirmationModal } from '../../../CommonComponents/DeleteConfirmationModal/DeleteConfirmationModal';
import { useToast } from '../../../Toast/context/useToast.context';
import locale from '../customReports-locale.json';
import { customReportsQueryKeys } from '../hooks/customReportsQueryKeys';
import { CustomReportPreview } from './CustomReportPreview';
import { styles } from './EditReportModal.styles';
import { useDeleteSpreadsheet } from './hooks/useDeleteSpreadsheet';
import { useFetchNamedRanges } from './hooks/useFetchNamedRanges';
import { useUpdateSpreadsheetName } from './hooks/useUpdateSpreadsheetName';

type Props = {
  spreadsheetId: string;
  publicationId: string;
  initialName: string;
  onClose: () => void;
};

export const EditReportModal_NamedRangesFragment = graphql(`
  fragment EditReportModal_NamedRangesFragment on spreadsheetNamedRanges {
    id
    name
  }
`);

export function EditReportModal({ spreadsheetId, publicationId, initialName, onClose }: Props) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  const [reportName, setReportName] = useState(initialName);
  const [selectedNamedRangeId, setSelectedNamedRangeId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { mutateAsync: updateSpreadsheet } = useUpdateSpreadsheetName();
  const { mutateAsync: deleteSpreadsheet } = useDeleteSpreadsheet({ publicationId });
  const { data: namedRangesFragment, isLoading: isLoadingNamedRanges } = useFetchNamedRanges({ spreadsheetId });
  const namedRanges = namedRangesFragment
    ? getFragmentData(EditReportModal_NamedRangesFragment, namedRangesFragment)
    : [];

  const hasChanges = reportName !== initialName;
  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await updateSpreadsheet({ id: spreadsheetId, name: reportName });
      void queryClient.invalidateQueries({ queryKey: customReportsQueryKeys.all });

      toggleToast({
        title: locale.success.customReportUpdated,
        body: locale.success.customReportUpdatedBody,
        severity: 'success'
      });

      onClose();
    } catch (error) {
      console.error(error);
      toggleToast({
        title: locale.errors.somethingWentWrong,
        body: locale.errors.unableToUpdateReport,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteSpreadsheet({ spreadsheetId });
      void queryClient.invalidateQueries({ queryKey: customReportsQueryKeys.all });

      toggleToast({
        title: locale.success.customReportDeleted,
        body: locale.success.customReportDeletedBody,
        severity: 'success'
      });

      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      console.error(error);
      toggleToast({
        title: locale.errors.somethingWentWrong,
        body: locale.errors.unableToDeleteReport,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        fullWidth={true}
        data-test="edit-custom-spreadsheet-report-modal"
        disableEscapeKeyDown
        sx={styles.dialog}
      >
        <DialogTitle sx={styles.dialogTitle}>
          {locale.editReportModal.title}
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<SvgIcon component={CloseIcon} inheritViewBox />}
            sx={styles.actionButton}
            data-test="edit-report-modal-close-button"
          >
            {locale.editReportModal.close}
          </Button>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent} dividers={true}>
          <Box sx={styles.leftPanel}>
            <TextField
              label={locale.editReportModal.customReportName}
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              sx={styles.textField}
              inputProps={{ className: 'no-format' }}
              data-test="edit-report-modal-name-input"
            />

            <TextField
              label={locale.editReportModal.namedRanges}
              value={selectedNamedRangeId}
              onChange={(e) => setSelectedNamedRangeId(e.target.value)}
              select
              SelectProps={{ displayEmpty: true }}
              sx={styles.textField}
              inputProps={{ className: 'no-format' }}
              disabled={isLoadingNamedRanges || !namedRanges || namedRanges.length === 0}
              InputProps={{
                startAdornment: isLoadingNamedRanges ? (
                  <InputAdornment position="start">
                    <CircularProgress {...circularProgressSizeProps.extraXSmall} />
                  </InputAdornment>
                ) : undefined
              }}
              data-test="edit-report-modal-named-ranges-select"
            >
              <MenuItem value="" disabled>
                {locale.editReportModal.selectNamedRange}
              </MenuItem>
              {namedRanges?.map((range) => (
                <MenuItem key={range.id} value={range.id}>
                  {range.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<SvgIcon component={DeleteIcon} inheritViewBox />}
              onClick={() => setShowDeleteModal(true)}
              sx={styles.deleteButton}
              data-test="edit-report-modal-delete-button"
            >
              {locale.editReportModal.deleteReport}
            </Button>
          </Box>

          <Box sx={styles.rightPanel}>
            <CustomReportPreview
              publicationId={publicationId}
              spreadsheetId={spreadsheetId}
              selectedNamedRangeId={selectedNamedRangeId}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={styles.actionButton}
            color="secondary"
            data-test="edit-report-modal-cancel-button"
          >
            {locale.editReportModal.cancel}
          </Button>
          <Button
            onClick={handleSave}
            variant="outlined"
            sx={styles.actionButton}
            disabled={!reportName || isSaving || !hasChanges}
            endIcon={
              isSaving ? <CircularProgress {...circularProgressSizeProps.extraSmall} sx={styles.loadingIcon} /> : null
            }
            data-test="edit-report-modal-save-button"
          >
            {locale.editReportModal.saveChanges}
          </Button>
        </DialogActions>
      </Dialog>

      {showDeleteModal ? (
        <DeleteConfirmationModal
          title={locale.editReportModal.deleteModalTitle}
          message={
            <Box sx={styles.deleteMessage}>
              <Typography variant="body1">{locale.editReportModal.deletingIsPermanent}</Typography>
              <Typography variant="body1">{locale.editReportModal.allStoriesAffected}</Typography>
            </Box>
          }
          isDeleting={isDeleting}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDeleteConfirm}
        />
      ) : null}
    </>
  );
}
